import { useEffect, useId, useRef, useState } from "react";
import { FieldLabel } from "@/components/ds/EditableField";
import { formatAddress } from "@/lib/address";
import { copy } from "@/lib/copy";
import { fetchJson } from "@/lib/http";

/**
 * L'entreprise se CHOISIT, elle ne se saisit pas.
 *
 * Trois lettres, une ligne cliquée, et le nom, le SIREN, le SIRET et l'adresse sont
 * remplis d'un coup. C'est le geste le plus court possible, et il rend en prime une
 * donnée propre : une raison sociale exacte plutôt que « sncf » ou « SNCF Voyageurs
 * (service RH) », un SIREN qui permet de rapprocher deux dépôts de la même boîte.
 *
 * L'annuaire ne connaît que la France. Une entreprise étrangère garde donc un chemin
 * complet : la saisie à la main, sans identifiant, qui reste un dépôt valide.
 */

export type CompanyIdentity = {
	name: string;
	siren?: string | null;
	siret?: string | null;
	address?: string | null;
	postalCode?: string | null;
	city?: string | null;
};

type Match = CompanyIdentity & { siren: string; closed: boolean };

const MIN_QUERY = 3;
/** Assez court pour suivre la frappe, assez long pour ne pas appeler à chaque lettre. */
const DEBOUNCE_MS = 250;

const place = formatAddress;

export function CompanyPicker({
	value,
	error,
	onChange,
}: {
	value: CompanyIdentity | null;
	error?: string;
	onChange: (value: CompanyIdentity | null) => void;
}) {
	const listId = useId();
	const inputId = "companyName";
	const [query, setQuery] = useState(value?.siren ? "" : (value?.name ?? ""));
	const [matches, setMatches] = useState<Match[]>([]);
	const [open, setOpen] = useState(false);
	const [highlight, setHighlight] = useState(0);
	const [searching, setSearching] = useState(false);
	const [unavailable, setUnavailable] = useState(false);
	const [manual, setManual] = useState(false);
	/** La recherche dont `matches` est la réponse : une liste vide d'AVANT la frappe ne dit rien. */
	const [resultatsPour, setResultatsPour] = useState<string | null>(null);
	const box = useRef<HTMLDivElement>(null);

	const chosen = Boolean(value?.siren);

	useEffect(() => {
		if (chosen || manual || query.trim().length < MIN_QUERY) {
			setMatches([]);
			setSearching(false);
			return;
		}
		const controller = new AbortController();
		const timer = setTimeout(async () => {
			setSearching(true);
			try {
				const body = await fetchJson<{ results?: Match[] }>(
					`/api/companies?q=${encodeURIComponent(query.trim())}`,
					{ signal: controller.signal },
				);
				setUnavailable(false);
				setMatches(body.results ?? []);
				setResultatsPour(query.trim());
				setHighlight(0);
				setOpen(true);
			} catch {
				// Une frappe suivante a annulé celle-ci : ce n'est pas une panne, et
				// afficher « l'annuaire ne répond pas » à chaque lettre serait faux.
				if (controller.signal.aborted) return;
				// L'annuaire n'a pas répondu. On ferme la liste plutôt que de la montrer
				// vide : « Aucune entreprise à ce nom » affirmerait que l'entreprise
				// n'existe pas, alors qu'on n'en sait rien. La ligne d'aide, elle, passe
				// à « L'annuaire ne répond pas » et garde la saisie à la main ouverte.
				setUnavailable(true);
				setMatches([]);
				setOpen(false);
			} finally {
				setSearching(false);
			}
		}, DEBOUNCE_MS);

		return () => {
			controller.abort();
			clearTimeout(timer);
		};
	}, [query, chosen, manual]);

	useEffect(() => {
		function onClickOutside(event: MouseEvent) {
			if (!box.current?.contains(event.target as Node)) setOpen(false);
		}
		document.addEventListener("mousedown", onClickOutside);
		return () => document.removeEventListener("mousedown", onClickOutside);
	}, []);

	/**
	 * La liste se ferme aussi quand le FOCUS quitte la boîte, pas seulement au clic
	 * extérieur. Au clavier (Tab vers « Votre nom ») ou à l'envoi, elle restait
	 * ouverte et recouvrait les deux champs suivants.
	 */
	function onBlurBox(event: React.FocusEvent<HTMLInputElement>) {
		if (!box.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
	}

	function choose(match: Match) {
		onChange({
			name: match.name,
			siren: match.siren,
			siret: match.siret ?? null,
			address: match.address ?? null,
			postalCode: match.postalCode ?? null,
			city: match.city ?? null,
		});
		setOpen(false);
		setMatches([]);
	}

	function onKeyDown(event: React.KeyboardEvent) {
		// Échap ferme TOUJOURS, liste vide comprise : le test du dessous la laissait
		// ouverte dès qu'il n'y avait aucun résultat à parcourir.
		if (event.key === "Escape") {
			setOpen(false);
			return;
		}
		if (!open || matches.length === 0) return;
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setHighlight((current) => (current + 1) % matches.length);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setHighlight((current) => (current - 1 + matches.length) % matches.length);
		} else if (event.key === "Enter") {
			event.preventDefault();
			const match = matches[highlight];
			if (match) choose(match);
		}
	}

	/**
	 * « Aucune entreprise à ce nom » ne s'ouvre plus en menu par-dessus le
	 * formulaire : il prend la place de l'aide, sous le champ. Un menu qui n'offre
	 * rien à choisir n'a pas à recouvrir « Votre nom » et « Votre email ».
	 */
	const sansResultat =
		!unavailable &&
		matches.length === 0 &&
		query.trim().length >= MIN_QUERY &&
		resultatsPour === query.trim();

	if (chosen && value) {
		return (
			<div className="sm:col-span-2">
				<FieldLabel label={copy.company.label} required />
				<div className="flex items-start justify-between gap-4 rounded-card border border-mg-border bg-mg-surface px-4 py-3">
					<div className="min-w-0">
						<p className="t-button break-words text-mg-heading">{value.name}</p>
						<p className="t-caption mt-0.5 text-mg-secondary">
							{copy.company.siren} {value.siren}
							{place(value) ? ` · ${place(value)}` : ""}
						</p>
					</div>
					<button
						type="button"
						className="t-label shrink-0 text-mg-action underline-offset-4 hover:underline"
						onClick={() => {
							setQuery("");
							onChange(null);
						}}
					>
						{copy.company.change}
					</button>
				</div>
			</div>
		);
	}

	const aideId = `${inputId}-aide`;

	if (manual) {
		return (
			<div className="sm:col-span-2">
				<FieldLabel htmlFor={inputId} label={copy.company.label} required />
				<input
					id={inputId}
					className="mg-field"
					aria-required="true"
					aria-invalid={Boolean(error)}
					aria-describedby={error ? aideId : undefined}
					value={value?.name ?? ""}
					onChange={(event) => onChange({ name: event.target.value })}
				/>
				{error ? (
					<p id={aideId} role="alert" className="t-caption-strong mt-1 text-mg-error-fg">
						{error}
					</p>
				) : (
					<button
						type="button"
						className="t-caption mt-1 text-mg-action underline-offset-4 hover:underline"
						onClick={() => {
							setManual(false);
							onChange(null);
						}}
					>
						{copy.company.backToSearch}
					</button>
				)}
			</div>
		);
	}

	return (
		<div className="relative sm:col-span-2" ref={box}>
			<FieldLabel htmlFor={inputId} label={copy.company.label} required />
			<input
				id={inputId}
				className="mg-field"
				role="combobox"
				aria-expanded={open && matches.length > 0}
				aria-controls={listId}
				aria-autocomplete="list"
				aria-busy={searching || undefined}
				aria-required="true"
				aria-invalid={Boolean(error)}
				aria-describedby={error ? `${aideId}-erreur ${aideId}` : aideId}
				autoComplete="off"
				placeholder={copy.company.placeholder}
				value={query}
				onChange={(event) => {
					setQuery(event.target.value);
					onChange(event.target.value ? { name: event.target.value } : null);
				}}
				onFocus={() => setOpen(matches.length > 0)}
				onKeyDown={onKeyDown}
				onBlur={onBlurBox}
			/>

			{open && matches.length > 0 ? (
				<div
					id={listId}
					// Un `div` et non un `ul` : le rôle `listbox` attend des `option`, et une
					// liste HTML sous ce rôle est signalée comme non interactive.
					role="listbox"
					aria-label={copy.company.label}
					className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-card border border-mg-border bg-mg-surface py-1 shadow-card"
				>
					{matches.map((match, index) => (
						<button
							key={`${match.siren}-${match.siret ?? index}`}
							type="button"
							role="option"
							aria-selected={index === highlight}
							className={`block w-full px-4 py-2.5 text-left ${
								index === highlight ? "bg-mg-subtle" : ""
							}`}
							onMouseEnter={() => setHighlight(index)}
							// Le focus reste dans le champ : sous Safari, un bouton cliqué ne
							// prend pas le focus, et la sortie du champ fermait la liste AVANT
							// que le clic n'arrive sur l'entreprise choisie.
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => choose(match)}
						>
							<span className="t-body-md block text-mg-heading">
								{match.name}
								{match.closed ? (
									<span className="t-caption ml-2 text-mg-error-fg">{copy.company.closed}</span>
								) : null}
							</span>
							<span className="t-caption block text-mg-secondary">
								{match.siren}
								{place(match) ? ` · ${place(match)}` : ""}
							</span>
						</button>
					))}
				</div>
			) : null}

			{/*
			 * ⚠️ Le refus du serveur et la ligne d'aide s'affichent ENSEMBLE, ils ne se
			 * remplacent pas. Le ternaire effaçait toute l'aide dès qu'une erreur de
			 * champ était posée, et cette erreur reste affichée jusqu'au dépôt suivant :
			 * l'entreprise dont le nom manquait perdait à la fois « L'annuaire ne répond
			 * pas » et le seul chemin de secours, « Mon entreprise n'est pas en France ».
			 * Elle tapait dans un champ devenu muet, sans savoir que l'annuaire était en
			 * panne ni qu'une saisie à la main existait.
			 */}
			{error ? (
				<p id={`${aideId}-erreur`} role="alert" className="t-caption-strong mt-1 text-mg-error-fg">
					{error}
				</p>
			) : null}
			<p id={aideId} className="t-caption mt-1 text-mg-secondary">
				{unavailable
					? copy.company.unavailable
					: sansResultat
						? copy.company.noResult
						: copy.company.hint}{" "}
				<button
					type="button"
					className="text-mg-action underline-offset-4 hover:underline"
					onClick={() => setManual(true)}
				>
					{copy.company.manualCta}
				</button>
			</p>
		</div>
	);
}
